import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { ArrowLeft, Send } from "lucide-solid";

import { Link } from "@/lib/u-router";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { createPowerSyncQuery } from "@famidex/shared/solid";
import {
  getMessagesQuery,
  getThreadByIdQuery,
  markThreadReadMutation,
  otherParticipant,
  queryKeys,
  sendMessageMutation,
} from "@famidex/shared";

/**
 * 💬 Conversación (hilo DM). Local-first [L]; sólo el emisor y el receptor
 * reciben los mensajes (privacidad de DM garantizada por el stream). El envío va
 * por `sendMessageMutation`; el servidor RE-DERIVA el destinatario de la
 * pertenencia al hilo. Traducción del diseño Stitch `conversacion_de_chat`.
 */
export function ConversationPage(props: { threadId: string }) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { t } = useI18n();
  const userId = session.userId;

  const [draft, setDraft] = createSignal("");
  const [busy, setBusy] = createSignal(false);

  const enabled = () => Boolean(drizzle() && userId());

  const threadRows = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.chat.thread(props.threadId),
    queryFn: () => {
      const d = drizzle();
      return d ? getThreadByIdQuery(d, props.threadId) : null;
    },
  });

  const messages = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.chat.messages(props.threadId),
    queryFn: () => {
      const d = drizzle();
      return d ? getMessagesQuery(d, props.threadId) : null;
    },
  });

  const other = createMemo(() => {
    const thread = threadRows.data?.[0];
    return thread ? otherParticipant(thread, userId() ?? "") : null;
  });

  // Marca leídos los mensajes recibidos al abrir / al llegar nuevos.
  createEffect(() => {
    if ((messages.data ?? []).length === 0) {
      return;
    }
    const d = drizzle();
    const uid = userId();
    if (d && uid) {
      void markThreadReadMutation(d, props.threadId, uid);
    }
  });

  const send = async () => {
    const text = draft().trim();
    const d = drizzle();
    const uid = userId();
    const partner = other();
    if (!text || !d || !uid || !partner?.id) {
      return;
    }
    setBusy(true);
    try {
      await sendMessageMutation(d, props.threadId, uid, partner.id, text);
      setDraft("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="flex min-h-[70dvh] flex-col" data-testid="conversation-page">
      <header class="flex items-center gap-3 border-b border-border/60 pb-3">
        <Link
          to="/chat"
          aria-label={t(msg`Volver a mensajes`)}
          class="grid h-9 w-9 place-items-center rounded-full text-muted-foreground hover:bg-muted"
        >
          <ArrowLeft class="h-5 w-5" />
        </Link>
        <h1 class="truncate text-lg font-semibold text-foreground" data-testid="conversation-title">
          {other()?.name ?? t(msg`Conversación`)}
        </h1>
      </header>

      <div class="flex flex-1 flex-col gap-2 overflow-y-auto py-4">
        <Show
          when={(messages.data ?? []).length > 0}
          fallback={
            <p
              data-testid="conversation-empty"
              class="my-auto text-center text-sm text-muted-foreground"
            >
              {t(msg`Aún no hay mensajes. Rompe el hielo.`)}
            </p>
          }
        >
          <For each={messages.data}>
            {(m) => {
              const mine = () => m.sender_id === userId();
              return (
                <div
                  data-testid="chat-message"
                  data-mine={mine() ? "true" : "false"}
                  class="max-w-[80%] rounded-2xl px-3.5 py-2 text-sm"
                  classList={{
                    "self-end bg-primary text-primary-foreground": mine(),
                    "self-start bg-card border border-border/60 text-foreground": !mine(),
                  }}
                >
                  {m.body}
                </div>
              );
            }}
          </For>
        </Show>
      </div>

      <form
        class="flex items-center gap-2 border-t border-border/60 pt-3"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <input
          type="text"
          data-testid="chat-input"
          value={draft()}
          onInput={(e) => setDraft(e.currentTarget.value)}
          placeholder={t(msg`Escribe un mensaje…`)}
          class="flex-1 rounded-full border border-border/60 bg-card px-4 py-2.5 text-sm text-foreground outline-none focus:border-primary"
        />
        <button
          type="submit"
          data-testid="chat-send"
          disabled={busy() || draft().trim().length === 0}
          aria-label={t(msg`Enviar`)}
          class="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground disabled:opacity-50"
        >
          <Send class="h-5 w-5" />
        </button>
      </form>
    </div>
  );
}
